import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DataTable from "components/common/tables/DataTable";
import { AppButton } from "components/common/buttons";
import { PERMISSIONS } from "auth/permissionCodes";
import { usePermissions } from "hooks/usePermissions";
import { catalogApi, departmentLeaderOptions, normalizeDepartmentUnit, normalizeResponseError } from "../../services/catalogs/systemCatalog.service";
import CatalogFormDialog from "./CatalogFormDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

const createUnitFields = (leaderOptions, row) => [
  { name: "departmentUnitCode", label: "Mã đơn vị trực thuộc", maxLength: 20 },
  { name: "departmentUnitName", label: "Tên đơn vị trực thuộc", required: true, maxLength: 255 },
  { name: "leaderUserId", label: "Người phụ trách", type: "select", options: departmentLeaderOptions(leaderOptions, row) },
  { name: "phone", label: "Số điện thoại", maxLength: 20 },
  { name: "email", label: "Email", maxLength: 100 },
  { name: "description", label: "Mô tả", multiline: true, maxLength: 1000 },
];

const DepartmentUnitsPanel = ({ department, leaderOptions, onSnackbar }) => {
  const permissions = usePermissions();
  const canCreate = permissions.can(PERMISSIONS.SYSTEM.DEPARTMENT_UNIT_CREATE);
  const canUpdate = permissions.can(PERMISSIONS.SYSTEM.DEPARTMENT_UNIT_UPDATE);
  const canDelete = permissions.can(PERMISSIONS.SYSTEM.DEPARTMENT_UNIT_DELETE);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({ open: false, mode: "create", row: null });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadUnits = useCallback(async () => {
    setLoading(true);
    try {
      const data = await catalogApi.getDepartmentUnits(department.departmentId);
      setRows(data.map(normalizeDepartmentUnit));
    } catch (error) {
      setRows([]);
      onSnackbar(normalizeResponseError(error, "Không thể tải danh sách đơn vị trực thuộc"), "error");
    } finally {
      setLoading(false);
    }
  }, [department.departmentId, onSnackbar]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  const handleSubmit = async (values) => {
    if (submitting || (formState.mode === "edit" ? !canUpdate : !canCreate)) return;
    setSubmitting(true);
    try {
      const payload = {
        departmentId: department.departmentId,
        departmentUnitCode: String(values.departmentUnitCode || "").trim() || null,
        departmentUnitName: String(values.departmentUnitName || "").trim(),
        leaderUserId: values.leaderUserId ? Number(values.leaderUserId) : null,
        phone: String(values.phone || "").trim() || null,
        email: String(values.email || "").trim() || null,
        description: String(values.description || "").trim() || null,
      };

      if (formState.mode === "edit") {
        await catalogApi.updateDepartmentUnit(formState.row.departmentUnitId, payload);
        onSnackbar("Cập nhật đơn vị trực thuộc thành công");
      } else {
        await catalogApi.createDepartmentUnit(payload);
        onSnackbar("Thêm đơn vị trực thuộc thành công");
      }

      setFormState({ open: false, mode: "create", row: null });
      await loadUnits();
    } catch (error) {
      onSnackbar(normalizeResponseError(error, "Không thể lưu đơn vị trực thuộc"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || submitting || !canDelete) return;
    setSubmitting(true);
    try {
      await catalogApi.deleteDepartmentUnit(deleteTarget.departmentUnitId);
      setDeleteTarget(null);
      onSnackbar("Xóa đơn vị trực thuộc thành công");
      await loadUnits();
    } catch (error) {
      onSnackbar(normalizeResponseError(error, "Không thể xóa đơn vị trực thuộc"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const fields = useMemo(() => createUnitFields(leaderOptions, formState.row), [leaderOptions, formState.row]);

  const columns = useMemo(
    () => [
      { accessorKey: "departmentUnitCode", header: "Mã đơn vị trực thuộc", size: 120, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "departmentUnitName", header: "Tên đơn vị trực thuộc", size: 240 },
      { accessorKey: "leaderFullName", header: "Người phụ trách", size: 160, Cell: ({ row }) => row.original.leaderFullName || row.original.leaderUserName || "-" },
      { accessorKey: "phone", header: "Số điện thoại", size: 120, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "email", header: "Email", size: 180, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "description", header: "Mô tả", size: 280, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
    []
  );

  return (
    <Box sx={{ p: 1.5, backgroundColor: "#f8fafc", borderTop: "1px solid #e5edf5" }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
          <Chip label={`Đơn vị trực thuộc ${department.departmentName}`} size="small" sx={{ alignSelf: { xs: "flex-start", sm: "center" } }} />
        </Stack>
        <DataTable
          columns={columns}
          data={rows}
          enableRowActions={canUpdate || canDelete}
          enablePagination={false}
          enableBottomToolbar={false}
          maxTableHeight="320px"
          state={{ isLoading: loading, showProgressBars: submitting }}
          renderTopToolbarExtraActions={() => canCreate ? (
              <AppButton
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setFormState({ open: true, mode: "create", row: null })}
                disabled={loading || submitting}
              >
                Thêm đơn vị trực thuộc
              </AppButton>
          ) : null}
          renderRowActions={({ row }) => (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              {canUpdate && <Tooltip title="Sửa">
                <IconButton size="small" color="primary" onClick={() => setFormState({ open: true, mode: "edit", row: row.original })}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>}
              {canDelete && <Tooltip title="Xóa">
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.original)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>}
            </Stack>
          )}
        />
      </Stack>

      <CatalogFormDialog
        open={formState.open}
        title={formState.mode === "edit" ? "Cập nhật đơn vị trực thuộc" : "Thêm đơn vị trực thuộc"}
        fields={fields}
        initialValues={formState.row}
        submitting={submitting}
        onClose={() => { if (!submitting) setFormState({ open: false, mode: "create", row: null }); }}
        onSubmit={handleSubmit}
      />
      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        message="Bạn có chắc chắn muốn xóa đơn vị trực thuộc này?"
        submitting={submitting}
        onClose={() => { if (!submitting) setDeleteTarget(null); }}
        onConfirm={handleDelete}
      />
    </Box>
  );
};

export default DepartmentUnitsPanel;
